import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const ChatPage = () => {
  const { t } = useTranslation();
  const { chatId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [activeChat, setActiveChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [ws, setWs] = useState(null);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (chatId) {
      fetchMessages(chatId);
      const chat = chats.find(c => c.id === chatId);
      setActiveChat(chat);
    }
  }, [chatId, chats]);

  useEffect(() => {
    // Connect to WebSocket
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const websocket = new WebSocket(`${wsUrl}/ws/chat/${user?.id}`);
    
    websocket.onopen = () => {
      console.log("WebSocket connected");
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "message" && data.message.chat_id === chatId) {
        setMessages(prev => [...prev, data.message]);
        scrollToBottom();
        // Send read receipt
        if (websocket.readyState === WebSocket.OPEN) {
          websocket.send(JSON.stringify({
            type: "read",
            chat_id: chatId,
            message_ids: [data.message.id]
          }));
        }
      } else if (data.type === "error") {
        toast.error(data.message);
      } else if (data.type === "typing" && data.chat_id === chatId) {
        setOtherUserTyping(data.is_typing);
      } else if (data.type === "read_receipt") {
        // Update message read status
        setMessages(prev => prev.map(m => 
          m.id === data.message_id ? { ...m, read: true, read_at: data.read_at } : m
        ));
      }
    };

    websocket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    setWs(websocket);

    return () => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, [user?.id, chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle typing indicator
  const sendTypingIndicator = useCallback((isTyping) => {
    if (ws && ws.readyState === WebSocket.OPEN && activeChat) {
      const otherUserId = activeChat.user1_id === user?.id ? activeChat.user2_id : activeChat.user1_id;
      ws.send(JSON.stringify({
        type: "typing",
        chat_id: chatId,
        to_user_id: otherUserId,
        is_typing: isTyping
      }));
    }
  }, [ws, activeChat, chatId, user?.id]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    sendTypingIndicator(true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false);
    }, 2000);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChats = async () => {
    try {
      const response = await axios.get(`${API}/chat/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChats(response.data);
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (id) => {
    try {
      const response = await axios.get(`${API}/chat/${id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !chatId) return;

    setSendingMessage(true);

    try {
      // Try WebSocket first
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Clear typing indicator
        sendTypingIndicator(false);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        
        ws.send(JSON.stringify({
          type: "message",
          chat_id: chatId,
          content: newMessage,
          to_user_id: activeChat?.other_user?.id
        }));
        
        // Optimistically add message
        const tempMessage = {
          id: Date.now().toString(),
          chat_id: chatId,
          sender_id: user.id,
          content: newMessage,
          read: false,
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, tempMessage]);
        setNewMessage("");
      } else {
        // Fallback to REST API
        const response = await axios.post(
          `${API}/chat/${chatId}/send`,
          { to_user_id: activeChat?.other_user?.id, content: newMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const newMsg = {
          id: response.data.message_id,
          chat_id: chatId,
          sender_id: user.id,
          content: newMessage,
          created_at: response.data.created_at
        };
        setMessages(prev => [...prev, newMsg]);
        setNewMessage("");
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Error sending message");
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const handleExchangeContact = async () => {
    try {
      await axios.post(`${API}/chat/${chatId}/exchange-contact`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contact exchange enabled! You can now share contact details.");
      fetchChats();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("No contact exchange credits. Please purchase.");
        navigate("/pricing");
      } else {
        toast.error(error.response?.data?.detail || "Error enabling contact exchange");
      }
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const hasActiveSubscription = user?.subscription && user?.subscription_expires && 
    new Date(user.subscription_expires) > new Date();

  return (
    <div className="min-h-screen bg-rose-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate("/dashboard")}
                className="text-rose-600 hover:text-rose-800 transition-colors font-medium"
                data-testid="back-btn"
              >
                ← Back
              </button>
              <Link to="/dashboard" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="SingleSeen" className="h-10 w-auto" />
              </Link>
            </div>
            <h1 className="font-serif text-xl text-rose-900">{t('nav.messages')}</h1>
          </div>
        </div>
      </div>

      <div className="flex-1 flex max-w-6xl mx-auto w-full">
        {/* Chat List */}
        <div className={`w-full md:w-80 bg-white border-r border-rose-100 ${chatId ? 'hidden md:block' : ''}`} data-testid="chat-list">
          <div className="p-4 border-b border-rose-100">
            <h2 className="font-semibold text-rose-900">{t('chat.conversations')}</h2>
          </div>

          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full skeleton" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-24 skeleton rounded" />
                    <div className="h-3 w-32 skeleton rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : chats.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl text-rose-300 font-serif">∞</span>
              </div>
              <p className="text-rose-600">{t('chat.noConversations')}</p>
              <Button 
                onClick={() => navigate("/browse")}
                className="mt-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
              >
                {t('common.browseProfiles')}
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-rose-100">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => navigate(`/chat/${chat.id}`)}
                  className={`p-4 cursor-pointer hover:bg-rose-50 transition-colors ${chatId === chat.id ? 'bg-rose-50' : ''}`}
                  data-testid={`chat-item-${chat.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-rose-200 overflow-hidden flex-shrink-0">
                      {chat.other_user?.photos?.[0] ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${chat.other_user.photos[0]}`}
                          alt={chat.other_user?.first_name}
                          className={`w-full h-full object-cover ${!hasActiveSubscription ? 'photo-blur' : ''}`}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-rose-400 bg-rose-100">
                          <span className="text-xl font-serif">{chat.other_user?.first_name?.[0] || "?"}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-rose-900 truncate">{chat.other_user?.first_name}</h3>
                      <p className="text-rose-500 text-sm truncate">
                        {chat.last_message?.content || t('chat.noMessages')}
                      </p>
                    </div>
                    <span className="text-rose-400">→</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col bg-rose-50 ${!chatId ? 'hidden md:flex' : 'flex'}`} data-testid="chat-area">
          {chatId && activeChat ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b border-rose-100 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => navigate("/chat")}
                    className="md:hidden text-rose-600"
                  >
                    ←
                  </button>
                  <div className="w-10 h-10 rounded-full bg-rose-200 overflow-hidden">
                    {activeChat.other_user?.photos?.[0] ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${activeChat.other_user.photos[0]}`}
                        alt={activeChat.other_user?.first_name}
                        className={`w-full h-full object-cover ${!hasActiveSubscription ? 'photo-blur' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-rose-400 bg-rose-100">
                        <span className="text-lg font-serif">{activeChat.other_user?.first_name?.[0] || "?"}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-rose-900">{activeChat.other_user?.first_name}</h3>
                    <p className="text-rose-500 text-xs">{activeChat.other_user?.country}</p>
                  </div>
                </div>

                {/* Contact Exchange Button */}
                {!activeChat.contact_exchanged && (
                  <Button
                    onClick={handleExchangeContact}
                    variant="outline"
                    size="sm"
                    className="border-amber-600 text-amber-600 hover:bg-amber-50 rounded-full text-xs"
                    data-testid="exchange-contact-btn"
                  >
                    Exchange Contact (£2.99)
                  </Button>
                )}
              </div>

              {/* Contact Detection Warning */}
              {!activeChat.contact_exchanged && (
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-amber-800 text-sm">
                  <span className="text-amber-600 font-medium">!</span>
                  <span>{t('chat.contactWarning')}</span>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-rose-500">{t('chat.noMessagesYet')}</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${msg.id}`}
                    >
                      <div className={msg.sender_id === user.id ? 'message-sent' : 'message-received'}>
                        <p>{msg.content}</p>
                        <div className={`flex items-center gap-1 mt-1 ${msg.sender_id === user.id ? 'justify-end' : ''}`}>
                          <span className={`text-xs ${msg.sender_id === user.id ? 'text-rose-200' : 'text-rose-400'}`}>
                            {formatTime(msg.created_at)}
                          </span>
                          {msg.sender_id === user.id && (
                            <span className="text-rose-200 text-xs" title={msg.read ? "Read" : "Delivered"}>
                              {msg.read ? "✓✓" : "✓"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                
                {/* Typing Indicator */}
                {otherUserTyping && (
                  <div className="flex justify-start">
                    <div className="message-received">
                      <div className="flex items-center gap-1">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={sendMessage} className="bg-white border-t border-rose-100 p-4">
                <div className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={handleInputChange}
                    placeholder={t('chat.typeMessage')}
                    className="flex-1 bg-rose-50 border-rose-200 h-12 rounded-full px-6"
                    disabled={sendingMessage}
                    data-testid="message-input"
                  />
                  <Button
                    type="submit"
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full w-12 h-12 p-0"
                    data-testid="send-message-btn"
                  >
                    →
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-4xl text-rose-300 font-serif">∞</span>
                </div>
                <h3 className="font-serif text-xl text-rose-900 mb-2">{t('chat.selectConversation')}</h3>
                <p className="text-rose-600">{t('chat.selectConversationDesc')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
